import { useEffect, useState } from 'react';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';

interface AlertProps {
  type: 'error' | 'success' | 'info';
  message: string;
  autoDismiss?: number;
  onDismiss?: () => void;
}

export function Alert({ type, message, autoDismiss, onDismiss }: AlertProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (autoDismiss) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onDismiss?.();
      }, autoDismiss);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, onDismiss]);

  if (!isVisible) return null;

  const styleMap = {
    error: {
      backgroundColor: '#fce8e8',
      borderColor: '#d9534f',
      borderLeftColor: '#d9534f',
      iconColor: '#d9534f',
      textColor: '#721c24',
    },
    success: {
      backgroundColor: '#f5f5f5',
      borderColor: '#ddd',
      borderLeftColor: '#28a745',
      iconColor: '#28a745',
      textColor: '#333',
    },
    info: {
      backgroundColor: '#e7f3ff',
      borderColor: '#b3d9ff',
      borderLeftColor: '#0078d4',
      iconColor: '#0078d4',
      textColor: '#003d82',
    },
  };

  const style = styleMap[type];
  const Icon = type === 'error' ? AlertCircle : type === 'success' ? CheckCircle : Info;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: style.backgroundColor,
        borderBottom: `1px solid ${style.borderColor}`,
        borderLeft: `4px solid ${style.borderLeftColor}`,
      }}
    >
      <div style={{
        /*maxWidth: '1100px',
        marginLeft: 'auto',
        marginRight: 'auto',*/
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '12px',
        paddingBottom: '12px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', minWidth: 0, flex: 1 }}>
          <Icon style={{ color: style.iconColor, width: '20px', height: '20px', flexShrink: 0 }} />
          <p style={{ color: style.textColor, fontSize: '13px', fontWeight: 500, margin: 0, lineHeight: '1.5', minWidth: 0 }}>{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            onDismiss?.();
          }}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            padding: '4px 8px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '12px',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = style.iconColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#999')}
          aria-label="Close notification"
        >
          <X style={{ width: '20px', height: '20px' }} />
        </button>
      </div>
    </div>
  );
}
